import { supabase } from '../lib/supabase';
import toastService from '../components/ui/Toast';

// Helper to convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
}

// Helper to convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
}

export const messageService = {
  // Get all conversations for a user
  async getConversations(userId) {
    try {
      // Fetch bookings where user is renter or owner
      const { data: bookings, error: bookingsError } = await supabase
        ?.from('bookings')
        ?.select(`
          id,
          booking_reference,
          start_date,
          end_date,
          status,
          payment_status,
          trailer_id,
          renter_id,
          owner_id,
          trailer_listing_id,
          trailer:trailer_listings!bookings_trailer_listing_id_fkey(
            id,
            title,
            trailer_type,
            photos
          )
        `)
        ?.or(`renter_id.eq.${userId},owner_id.eq.${userId}`)
        ?.in('status', ['confirmed', 'completed'])
        ?.eq('payment_status', 'succeeded')
        ?.order('created_at', { ascending: false });

      if (bookingsError) {
        console.error('Get bookings error:', bookingsError);
        if (bookingsError?.code === 'PGRST116') {
          return [];
        }
        throw new Error(bookingsError?.message || 'Failed to fetch conversations');
      }

      if (!bookings || bookings?.length === 0) {
        return [];
      }

      // Get messages for these bookings
      const bookingIds = bookings?.map(b => b?.id);
      const { data: messages, error: messagesError } = await supabase
        ?.from('messages')
        ?.select('*')
        ?.in('booking_id', bookingIds)
        ?.order('created_at', { ascending: false });

      if (messagesError) {
        console.error('Get messages error:', messagesError);
      }

      // Build conversations with participant info
      const conversations = await Promise.all(
        bookings?.map(async (booking) => {
          // Determine participant (the other person in conversation)
          const participantId = booking?.renter_id === userId ? booking?.owner_id : booking?.renter_id;
          
          // Fetch participant profile
          const { data: participant, error: participantError } = await supabase
            ?.from('user_profiles')
            ?.select('id, full_name, email, role, profile_photo_url')
            ?.eq('id', participantId)
            ?.single();

          if (participantError) {
            console.error('Get participant error:', participantError);
          }

          // Get messages for this booking
          const bookingMessages = messages?.filter(m => m?.booking_id === booking?.id) || [];
          const lastMessage = bookingMessages?.[0];

          // Count unread messages
          const unreadCount = bookingMessages?.filter(
            m => m?.receiver_id === userId && !m?.is_read
          )?.length || 0;

          // Get trailer image from photos array (trailer_listings uses 'photos' not 'images')
          const trailerImage = booking?.trailer?.photos?.[0] || '/assets/images/no_image.png';

          return {
            id: booking?.id,
            bookingId: booking?.id,
            bookingReference: booking?.booking_reference,
            participantId: participant?.id,
            participantName: participant?.full_name || 'Unknown User',
            participantEmail: participant?.email,
            participantRole: participant?.role,
            participantPhoto: participant?.profile_photo_url || null,
            trailerName: booking?.trailer?.title || 'Trailer',
            trailerImage,
            trailerImageAlt: `${booking?.trailer?.title || 'Trailer'} image`,
            rentalDates: `${new Date(booking?.start_date)?.toLocaleDateString()} - ${new Date(booking?.end_date)?.toLocaleDateString()}`,
            bookingStatus: booking?.status,
            status: booking?.status,
            lastMessage: lastMessage?.content || 'No messages yet',
            lastMessageTime: lastMessage?.created_at || booking?.created_at,
            unreadCount,
            messages: bookingMessages
          };
        })
      );

      return conversations;
    } catch (error) {
      console.error('Get conversations error:', error);
      throw error;
    }
  },

  // Get messages for a specific booking
  async getMessages(bookingId) {
    try {
      const { data, error } = await supabase
        ?.from('messages')
        ?.select(`
          *,
          sender:user_profiles!messages_sender_id_fkey(id, full_name, email),
          receiver:user_profiles!messages_receiver_id_fkey(id, full_name, email)
        `)
        ?.eq('booking_id', bookingId)
        ?.order('created_at', { ascending: true });

      if (error) {
        console.error('Get messages error:', error);
        if (error?.code === 'PGRST116') {
          return [];
        }
        throw new Error(error?.message || 'Failed to fetch messages');
      }

      return data ? data?.map(toCamelCase) : [];
    } catch (error) {
      console.error('Get messages error:', error);
      throw error;
    }
  },

  // Send a message
  async sendMessage(messageData) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const snakeCaseData = toSnakeCase(messageData);
      snakeCaseData.sender_id = user?.id;

      const { data, error } = await supabase
        ?.from('messages')
        ?.insert(snakeCaseData)
        ?.select(`
          *,
          sender:user_profiles!messages_sender_id_fkey(id, full_name, email),
          receiver:user_profiles!messages_receiver_id_fkey(id, full_name, email)
        `)
        ?.single();

      if (error) {
        console.error('Send message error:', error);
        throw new Error(error?.message || 'Failed to send message');
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Send message error:', error);
      throw error;
    }
  },

  // Mark messages as read
  async markAsRead(messageIds) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        ?.from('messages')
        ?.update({ is_read: true })
        ?.in('id', messageIds)
        ?.eq('receiver_id', user?.id)
        ?.select();

      if (error) {
        console.error('Mark as read error:', error);
        throw new Error(error?.message || 'Failed to mark messages as read');
      }

      return data ? data?.map(toCamelCase) : [];
    } catch (error) {
      console.error('Mark as read error:', error);
      throw error;
    }
  },

  // Get unread count for a user
  async getUnreadCount(userId) {
    try {
      const { count, error } = await supabase
        ?.from('messages')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('receiver_id', userId)
        ?.eq('is_read', false);

      if (error) {
        console.error('Get unread count error:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Get unread count error:', error);
      return 0;
    }
  },

  // Subscribe to new messages - returns raw Supabase payload so caller can handle INSERT/UPDATE events
  subscribeToMessages(userId, callback) {
    // Use unique channel name per user to avoid conflicts
    const channelName = `messages_user_${userId}`;
    const channel = supabase
      ?.channel(channelName)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${userId}`
        },
        async (payload) => {
          if (payload?.eventType === 'INSERT') {
            // Fetch sender details for toast notification
            const { data: sender } = await supabase
              ?.from('user_profiles')
              ?.select('full_name')
              ?.eq('id', payload?.new?.sender_id)
              ?.single();

            toastService?.messageDelivered?.({
              senderName: sender?.full_name || 'Someone'
            });
          }
          // Pass raw payload so caller can handle eventType and payload.new
          callback(payload);
        }
      )
      ?.subscribe();

    return channel;
  }
};
